/**
 * Shopify t126 (UX t129) : configurateur (bloc E). Fichier du thème : assets/md-configurateur.js
 * Chargé en module par snippets/md-configurateur.liquid, seulement sur les fiches qui ont un configurateur.
 *
 * - Récapitulatif (une ligne par choix) et total suivent les choix, en TTC ou en HT selon la bascule de la fiche
 *   (t7, assets/minutedrone-tax-toggle.js, clé locale « minutedrone-tax-mode ») ; lignes et total portent data-tax-price,
 *   la bascule les met donc à jour elle-même quand le visiteur change de mode.
 * - « Ajouter au panier » : un seul POST /cart/add.js avec items[] (variantes choisies ; quantité 1, ou celle du sélecteur « − 1 + » des accessoires, t530), puis panier relu et
 *   CartAddEvent comme « Compléter votre commande » (t92, vérifié au lot 5) et le formulaire d'achat d'Horizon :
 *   pastille du panier, tiroir rafraîchi (sections) et ouvert si le thème l'ouvre déjà après un ajout (réglage auto-open).
 * - Disposition « colonne » : le bouton d'achat du thème est masqué par le CSS mais garde sa place (hauteur nulle), donc la
 *   barre collante (sticky-add-to-cart) apparaît quand le récapitulatif sort de l'écran ; elle affiche le total et son
 *   bouton ajoute la configuration. Son clic est intercepté sur window en phase de capture, avant le gestionnaire
 *   on:click d'Horizon (délégué sur document en capture, assets/component.js) : l'ajout du seul produit n'a pas lieu.
 */
import { CartAddEvent, CartErrorEvent, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const SOURCE = 'md-configurateur';
const CLE_MODE_TAXE = 'minutedrone-tax-mode';

/** @returns {'ht' | 'ttc'} */
function modeTaxe() {
  try {
    return window.localStorage.getItem(CLE_MODE_TAXE) === 'ht' ? 'ht' : 'ttc';
  } catch (erreur) {
    return 'ttc';
  }
}

/** Même format que la bascule TTC / HT (Intl, fr-FR). */
function formaterPrix(centimes, devise) {
  const montant = (Number(centimes) || 0) / 100;
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: devise || 'EUR' }).format(montant);
  } catch (erreur) {
    return `${montant.toFixed(2)} ${devise || 'EUR'}`;
  }
}

function routes() {
  const theme = typeof Theme !== 'undefined' ? /** @type {any} */ (Theme) : null;
  const r = (theme && theme.routes) || {};
  return { cart_add_url: r.cart_add_url || '/cart/add.js', cart_url: r.cart_url || '/cart' };
}

async function lirePanier() {
  try {
    const reponse = await fetch(`${routes().cart_url}.js`, { headers: { Accept: 'application/json' } });
    return reponse.ok ? await reponse.json() : null;
  } catch (erreur) {
    return null;
  }
}

class MdConfigurateur extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controleur;

  /** @type {AbortController | null} */
  #envoi = null;

  #totalTtc = 0;
  #totalHt = 0;
  #premier = true;

  connectedCallback() {
    this.#controleur?.abort();
    this.#controleur = new AbortController();
    const { signal } = this.#controleur;

    this.bloc = this.querySelector('[data-md-cfg]');
    if (!this.bloc) return;
    this.colonne = this.bloc.classList.contains('md-cfg--colonne');
    this.bouton = this.querySelector('[data-md-ajouter]');

    this.addEventListener('change', (event) => this.#surChangement(event), { signal });
    // t530 : boutons « − / + » de la quantité des accessoires (hors du label : aucun effet sur la case).
    this.addEventListener('click', (event) => this.#surClicQte(event), { signal });
    this.bouton?.addEventListener('click', (event) => this.ajouter(event), { signal });

    if (this.colonne) {
      // Repli des navigateurs sans :has() : la classe fait le même masquage (assets/md-configurateur.css).
      this.closest('.product-details')?.classList.add('md-cfg-actif');
      window.addEventListener('click', this.#surClicBarre, { capture: true, signal });
    }
    // Bascule TTC / HT : le prix de la barre collante et l'annonce pour lecteurs d'écran n'ont pas data-tax-price,
    // la bascule ne les met donc pas à jour (correctif du 17/10/2026 pour l'annonce, restée en TTC en mode HT).
    // Écouteur en bulle sur document : la bascule a déjà enregistré le nouveau mode (écouteur posé sur le bouton).
    document.addEventListener(
      'click',
      (event) => {
        const cible = event.target instanceof Element ? event.target : null;
        if (!cible || !cible.closest('.md-tax-toggle__btn')) return;
        this.#majBarre();
        const annonce = this.querySelector('[data-md-annonce]');
        if (annonce && annonce.textContent) annonce.textContent = this.#texteTotal(modeTaxe());
      },
      { signal }
    );

    this.maj();
  }

  disconnectedCallback() {
    this.#controleur?.abort();
    this.#envoi?.abort();
  }

  /** t530 : quantité d'un choix (1 à 10) ; 1 pour les versions et protections (pas de sélecteur). */
  #qte(input) {
    const champ = input.closest('.md-cfg__accessoire')?.querySelector('[data-md-qte-val]');
    if (!(champ instanceof HTMLInputElement)) return 1;
    const n = Math.round(Number(champ.value));
    return Math.min(10, Math.max(1, Number.isFinite(n) ? n : 1));
  }

  /** t530 : fixe la quantité affichée, coche l'accessoire si elle passe au-dessus de 1, met à jour les boutons. */
  #poserQte(bloc, valeur) {
    const champ = bloc.querySelector('[data-md-qte-val]');
    const caseAcc = bloc.querySelector('input[type="checkbox"]');
    if (!(champ instanceof HTMLInputElement)) return;
    const n = Math.min(10, Math.max(1, Math.round(Number(valeur)) || 1));
    champ.value = String(n);
    if (n > 1 && caseAcc instanceof HTMLInputElement && !caseAcc.checked && !caseAcc.disabled) caseAcc.checked = true;
    bloc.querySelector('[data-md-qte="-1"]')?.toggleAttribute('disabled', n <= 1);
    bloc.querySelector('[data-md-qte="1"]')?.toggleAttribute('disabled', n >= 10);
  }

  #surClicQte(event) {
    const cible = event.target instanceof Element ? event.target.closest('[data-md-qte]') : null;
    const bloc = cible?.closest('.md-cfg__accessoire');
    if (!cible || !bloc) return;
    event.preventDefault();
    const champ = bloc.querySelector('[data-md-qte-val]');
    const actuelle = champ instanceof HTMLInputElement ? Number(champ.value) || 1 : 1;
    this.#poserQte(bloc, actuelle + Number(cible.getAttribute('data-md-qte')));
    this.maj();
  }

  #surChangement(event) {
    const cible = event.target;
    if (cible instanceof HTMLInputElement && cible.hasAttribute('data-md-qte-val')) {
      const bloc = cible.closest('.md-cfg__accessoire');
      if (bloc) this.#poserQte(bloc, cible.value);
    }
    this.maj();
  }

  /** @returns {HTMLInputElement[]} choix cochés et disponibles (la ligne « Sans … » a une valeur vide) */
  #choisis() {
    return Array.from(this.querySelectorAll('input:checked')).filter(
      (i) => i instanceof HTMLInputElement && i.value && !i.disabled
    );
  }

  /** Barre collante de la même section (sections/product-information.liquid). */
  #barre() {
    const section = this.closest('.shopify-section');
    return section ? section.querySelector('sticky-add-to-cart') : null;
  }

  /** Prix de la barre collante = total de la configuration, dans le mode choisi. */
  #majBarre() {
    if (!this.colonne) return;
    const prix = this.#barre()?.querySelector('.sticky-add-to-cart__price');
    if (prix) prix.textContent = formaterPrix(modeTaxe() === 'ht' ? this.#totalHt : this.#totalTtc, this.dataset.devise);
  }

  /** Annonce pour lecteurs d'écran : total dans le mode affiché, avec la mention HT ou TTC. */
  #texteTotal(mode) {
    const montant = formaterPrix(mode === 'ht' ? this.#totalHt : this.#totalTtc, this.dataset.devise);
    return `Total : ${montant} ${mode === 'ht' ? 'HT' : 'TTC'}`;
  }

  /** Récapitulatif, total, bouton. */
  maj() {
    const ul = this.querySelector('[data-md-lignes]');
    const total = this.querySelector('[data-md-total]');
    const annonce = this.querySelector('[data-md-annonce]');
    const mode = modeTaxe();
    const devise = this.dataset.devise;
    const liste = this.#choisis();

    let ttc = 0;
    let ht = 0;
    const lignes = liste.map((i) => {
      const qte = this.#qte(i);
      const prixTtc = (parseInt(i.dataset.prixTtc || '0', 10) || 0) * qte;
      const prixHt = (parseInt(i.dataset.prixHt || '0', 10) || 0) * qte;
      ttc += prixTtc;
      ht += prixHt;

      const li = document.createElement('li');
      li.className = 'md-cfg__ligne';
      const nom = document.createElement('span');
      nom.textContent = (i.dataset.nom || '') + (qte > 1 ? ` × ${qte}` : '');
      const prix = document.createElement('span');
      prix.setAttribute('data-tax-price', '');
      prix.dataset.priceTtc = String(prixTtc);
      prix.dataset.priceHt = String(prixHt);
      const valeur = document.createElement('span');
      valeur.setAttribute('data-tax-price-value', '');
      valeur.textContent = formaterPrix(mode === 'ht' ? prixHt : prixTtc, devise);
      prix.append(valeur);
      li.append(nom, prix);
      return li;
    });
    if (ul) ul.replaceChildren(...lignes);

    this.#totalTtc = ttc;
    this.#totalHt = ht;
    if (total instanceof HTMLElement) {
      total.dataset.priceTtc = String(ttc);
      total.dataset.priceHt = String(ht);
      const valeur = total.querySelector('[data-tax-price-value]');
      if (valeur) valeur.textContent = formaterPrix(mode === 'ht' ? ht : ttc, devise);
      const sousTexte = total.querySelector('[data-tax-subtext]');
      if (sousTexte) sousTexte.textContent = mode === 'ht' ? 'Hors Taxes.' : 'Taxes incluses.';
    }
    if (annonce && !this.#premier) annonce.textContent = this.#texteTotal(mode);
    this.#premier = false;
    if (this.bouton) this.bouton.disabled = liste.length === 0 || this.#envoi !== null;
    this.#majBarre();
  }

  /** Bouton de la barre collante : ajoute la configuration à la place du seul produit. */
  #surClicBarre = (event) => {
    const cible = event.target instanceof Element ? event.target : null;
    const bouton = cible && cible.closest('.sticky-add-to-cart__button');
    const barre = this.#barre();
    if (!bouton || !barre || !barre.contains(bouton)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.ajouter(event);
  };

  /** Un seul POST /cart/add.js avec items[], puis panier relu et CartAddEvent (comme t92 md-articles-lies.js). */
  async ajouter(event) {
    event?.preventDefault();
    if (this.#envoi) return;
    const choix = this.#choisis();
    if (!choix.length) return;

    const annonce = this.querySelector('[data-md-annonce]');
    const erreur = this.querySelector('[data-md-erreur]');
    if (erreur) {
      erreur.hidden = true;
      erreur.textContent = '';
    }

    this.#envoi = new AbortController();
    if (this.bouton) {
      this.bouton.disabled = true;
      this.bouton.setAttribute('aria-busy', 'true');
    }

    // Sections du panier à renvoyer (tiroir), comme product-form.js.
    const sections = Array.from(
      new Set(
        Array.from(document.querySelectorAll('cart-items-component'), (el) =>
          el instanceof HTMLElement ? el.dataset.sectionId || '' : ''
        ).filter(Boolean)
      )
    );
    const corps = { items: choix.map((i) => ({ id: Number(i.value), quantity: this.#qte(i) })) };
    if (sections.length) {
      corps.sections = sections.join(',');
      corps.sections_url = window.location.pathname;
    }

    try {
      const reponse = await fetch(routes().cart_add_url, {
        ...fetchConfig('json', { body: JSON.stringify(corps) }),
        signal: this.#envoi.signal,
      });
      const donnees = await reponse.json().catch(() => ({}));
      const icone = /** @type {any} */ (document.querySelector('cart-icon'));
      const nombreAvant = Number(icone && icone.currentCartCount) || 0;

      if (!reponse.ok || donnees.status) {
        const message = donnees.description || donnees.message || 'Ajout impossible';
        if (erreur) {
          erreur.textContent = message;
          erreur.hidden = false;
        }
        this.dispatchEvent(new CartErrorEvent(this.id, donnees.message, donnees.description, donnees.errors));
        const panier = await lirePanier();
        if (panier && panier.item_count !== nombreAvant) {
          this.dispatchEvent(
            new CartUpdateEvent(panier, this.id, { didError: true, source: SOURCE, itemCount: panier.item_count })
          );
        }
        return;
      }

      const panier = await lirePanier();
      if (annonce) annonce.textContent = 'Configuration ajoutée au panier';
      this.dispatchEvent(
        new CartAddEvent(panier || {}, this.id, {
          source: SOURCE,
          itemCount: panier ? panier.item_count : nombreAvant + choix.length,
          sections: donnees.sections,
        })
      );
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') return;
      console.error('t126 : ajout de la configuration impossible', e);
      if (erreur) {
        erreur.textContent = 'Ajout impossible';
        erreur.hidden = false;
      }
    } finally {
      this.#envoi = null;
      this.bouton?.removeAttribute('aria-busy');
      if (this.bouton) this.bouton.disabled = this.#choisis().length === 0;
    }
  }
}

if (!customElements.get('md-configurateur')) {
  customElements.define('md-configurateur', MdConfigurateur);
}
